"use client";

import { useState, useEffect } from "react";
import IkigaiFillInBlank from "../components/IkigaiFillInBlank";
import ResultMatrix from "../components/ResultMatrix";
import JohariAdjectiveSelection from "../components/JohariAdjectiveSelection";
import JohariWindowResults from "../components/JohariWindowResults";
import IkigaiVennChart from "../components/IkigaiVennChart";
import DevelopmentPlan from "../components/DevelopmentPlan";
import Sidebar from "../components/Sidebar";
import { useLocation } from "react-router-dom";
import { toast } from 'react-toastify';
// Header handled by shared Navbar
import Navbar from '../components/Navbar';
import '../Style/dashboard.css';
import api from "../sever/Api";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../components/card";
import { Button } from "../components/button";
import { Brain, ArrowRight } from "lucide-react";

export default function IkigaiApp() {
  const [ikigaiAnswers, setIkigaiAnswers] = useState({});
  const [ikigaiQuestions, setIkigaiQuestions] = useState([]);
  const [showIntro, setShowIntro] = useState(true);
  const [johariSelfAnswers, setJohariSelfAnswers] = useState([]);
  const [johariOthersAnswers, setJohariOthersAnswers] = useState({});
  const [shareableLink, setShareableLink] = useState("");
  const location = useLocation();
  const [showIkigaiResults, setShowIkigaiResults] = useState(false);
  const [showJohariResults, setShowJohariResults] = useState(false);
  const [activeTest, setActiveTest] = useState("ikigai");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("matrix");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const test = params.get("test");
    if (test === "johari" || test === "ikigai") {
      setActiveTest(test);
    }
  }, [location.search]);

  useEffect(() => {
    try {
      const storedUserId = localStorage.getItem('johari_current_user_id');
      const self = JSON.parse(localStorage.getItem(`johari_self_${storedUserId}`) || '[]');
      if (storedUserId) {
        const self = JSON.parse(localStorage.getItem(`johari_self_${storedUserId}`) || '[]');
        if (Array.isArray(self) && self.length >= 5) {
          setJohariSelfAnswers(self);
          const savedLink = localStorage.getItem('johari_share_link') || `${window.location.origin}/feedback/${storedUserId}`;
          setShareableLink(savedLink);

          if (!location.search.includes("test=")) {
            setShowJohariResults(true);
            setActiveTest('johari');
            setActiveTab('results');
          }
        }
      }
    } catch (_) { }
  }, []);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const johariAdjectives = [
    "Able", "Accepting", "Adaptable", "Adventurous", "Agreeable", "Angry", "Arrogant", "Artistic", "Attentive",
    "Balanced", "Bold", "Bossy", "Brave", "Calm", "Caring", "Careless", "Cheerful", "Clever", "Complex", "Confident",
    "Controlling", "Cooperative", "Cowardly", "Creative", "Cruel", "Curious", "Decisive", "Deceitful", "Dependable",
    "Dignified", "Dishonest", "Disrespectful", "Dynamic", "Egotistical", "Efficient", "Energetic", "Enthusiastic",
    "Extroverted", "Flexible", "Forgetful", "Friendly", "Funny", "Generous", "Gentle", "Giving", "Greedy", "Happy",
    "Handy", "Hardworking", "Helpful", "Honest", "Hopeful", "Humorous", "Idealistic", "Impatient", "Inconsiderate",
    "Indecisive", "Independent", "Ingenious", "Inflexible", "Intelligent", "Introverted", "Jealous", "Kind",
    "Knowledgeable", "Know-it-all", "Lazy", "Logical", "Loving", "Loyal", "Mean", "Mature", "Modest", "Moody",
    "Motivated", "Narcissistic", "Neat", "Nervous", "Observant", "Optimistic", "Organized", "Patient", "Pessimistic",
    "Persistent", "Playful", "Polite", "Practical", "Powerful", "Proud", "Quiet", "Reflective", "Relaxed", "Reliable",
    "Religious", "Reserved", "Resourceful", "Respectful", "Responsible", "Responsive", "Revengeful", "Rude",
    "Searching", "Self-assertive", "Self-conscious", "Selfish", "Sensitive", "Sensible", "Sentimental", "Shy", "Silly",
    "Sincere", "Smart", "Spontaneous", "Spiteful", "Stubborn", "Supportive", "Sympathetic", "Tactful", "Tense",
    "Thoughtful", "Trustworthy", "Understanding", "Unreliable", "Untrustworthy", "Vindictive", "Warm", "Wise", "Witty"
  ];

  const buildIkigaiAnswers = () => {
    const detailedAnswers = [];
    ikigaiQuestions.forEach((question, index) => {
      const answer = ikigaiAnswers[index];
      if (answer) {
        detailedAnswers.push({
          questionId: index.toString(),
          questionText: question,
          selectedOptionId: 'text',
          selectedOptionText: answer
        });
      }
    });
    return detailedAnswers;
  };

  const buildJohariAnswers = () => {
    const detailedAnswers = [];
    johariSelfAnswers.forEach((adjective, index) => {
      detailedAnswers.push({
        questionId: index.toString(),
        questionText: `Selected adjective ${index + 1}`,
        selectedOptionId: adjective,
        selectedOptionText: adjective
      });
    });
    return detailedAnswers;
  };

  const saveIkigaiResult = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to save your results.');
        return;
      }

      const detailedAnswers = buildIkigaiAnswers();
      const scores = {
        totalQuestions: ikigaiQuestions.length,
        answeredQuestions: Object.keys(ikigaiAnswers).length
      };

      const response = await api.post("/api/test-results", {
        testType: "ikigai",
        answers: detailedAnswers,
        scores,
        overallScore: Object.keys(ikigaiAnswers).length,
        overallPercentage: Math.round((Object.keys(ikigaiAnswers).length / ikigaiQuestions.length) * 100)
      });

      toast.success('Ikigai results saved successfully!');
    } catch (error) {
      console.error("Error saving Ikigai result:", error);
      toast.error(error.response?.data?.message || 'Failed to save Ikigai results');
    }
  };

  const saveJohariResult = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to save your results.');
        return;
      }

      const detailedAnswers = buildJohariAnswers();
      const scores = {
        selectedAdjectives: johariSelfAnswers.length,
        totalAdjectives: johariAdjectives.length
      };

      const response = await api.post("/api/test-results", {
        testType: "johari",
        answers: detailedAnswers,
        scores,
        overallScore: johariSelfAnswers.length,
        overallPercentage: Math.round((johariSelfAnswers.length / 20) * 100) // Assuming 20 is max adjectives
      });

      toast.success('Johari results saved successfully!');
    } catch (error) {
      console.error("Error saving Johari result:", error);
      toast.error(error.response?.data?.message || 'Failed to save Johari results');
    }
  };

  // Save results when they are first shown
  useEffect(() => {
    if (showIkigaiResults && Object.keys(ikigaiAnswers).length > 0) {
      saveIkigaiResult();
    }
  }, [showIkigaiResults, ikigaiAnswers]);

  useEffect(() => {
    if (showJohariResults && johariSelfAnswers.length > 0) {
      saveJohariResult();
    }
  }, [showJohariResults, johariSelfAnswers]);

  const handleIkigaiComplete = (answers, questions) => {
    setIkigaiAnswers(answers);
    setIkigaiQuestions(questions);
    setShowIkigaiResults(true);
  };

  const handleJohariSelfComplete = (selectedAdjectives, userId) => {
    if (!userId) return;

    setJohariSelfAnswers(selectedAdjectives);
    const link = `${window.location.origin}/feedback/${userId}`;
    setShareableLink(link);
    setShowJohariResults(true);
    try {
      localStorage.setItem('johari_current_user_id', userId);
      localStorage.setItem('johari_share_link', link);
    } catch (_) { }

    const isIkigaiComplete = Object.keys(ikigaiAnswers).length >= ikigaiQuestions.length;
    if (isIkigaiComplete) {
      setActiveTab("development");
    } else {
      setActiveTab("results");
    }

    localStorage.setItem(`johari_self_${userId}`, JSON.stringify(selectedAdjectives));
  };

  const handleReset = () => {
    setIkigaiAnswers({});
    setIkigaiQuestions([]);
    setJohariSelfAnswers([]);
    setJohariOthersAnswers({});
    setShareableLink("");
    setShowIkigaiResults(false);
    setShowJohariResults(false);
    try {
      const uid = localStorage.getItem('johari_current_user_id');
      if (uid) {
        localStorage.removeItem('johari_self_' + uid);
      }
      localStorage.removeItem('johari_current_user_id');
      localStorage.removeItem('johari_share_link');
    } catch (_) { }
  };

  const switchTest = (test) => {
    setActiveTest(test);
    setActiveTab("matrix");
    setShowIkigaiResults(false);
    setShowJohariResults(false);
  };

  const copyShareableLink = () => {
    navigator.clipboard.writeText(shareableLink);
    toast.success('Link copied to clipboard!');
  };

  const ikigaiCompleted = Object.keys(ikigaiAnswers).length >= ikigaiQuestions.length;
  const johariCompleted = johariSelfAnswers.length >= 5;
  const bothCompleted = ikigaiCompleted && johariCompleted;

  const showTab = (name) => setActiveTab(name);

  const userId = shareableLink ? String(shareableLink.split("/").pop()) : "";
  const isResultView = (activeTest === "ikigai" && showIkigaiResults) || (activeTest === "johari" && showJohariResults);

  if (showIntro) {
    return (
      <div className="min-h-screen w-full overflow-x-hidden bg-gradient-to-br from-[#EFF6FF] via-white to-[#F9FAFB] relative">
        {/* Sidebar */}
        <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
          <Sidebar />
        </div>

        {/* Main content */}
        <div className="ml-64">
          <Navbar />

          <div className="w-full max-w-7xl mx-auto pt-10 pb-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl mx-auto">
              <Card className="text-center bg-white shadow-lg">
                <CardHeader className="space-y-4">
                  <CardTitle className="text-3xl font-bold">
                    {activeTest === "ikigai" ? "IKIGAI Finder" : "Johari Window"}
                  </CardTitle>
                  <CardDescription className="text-lg text-gray-600">
                    {activeTest === "ikigai"
                      ? "Uncover your passions, skills, and purpose for a meaningful life."
                      : "Discover how you and others perceive your personality traits."}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-6">
                  {activeTest === "ikigai" ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                      <div className="space-y-2">
                        <h3 className="font-semibold text-red-600">Love</h3>
                        <p className="text-sm text-gray-600">Discover what you truly enjoy doing</p>
                      </div>
                      <div className="space-y-2">
                        <h3 className="font-semibold text-yellow-600">Good At</h3>
                        <p className="text-sm text-gray-600">Identify your skills and strengths</p>
                      </div>
                      <div className="space-y-2">
                        <h3 className="font-semibold text-green-600"> World Needs
                        </h3>
                        <p className="text-sm text-gray-600">Explore how your talents can serve others</p>
                      </div>
                      <div className="space-y-2">
                        <h3 className="font-semibold text-blue-600">Paid For</h3>
                        <p className="text-sm text-gray-600">Understand where your passions meet opportunities</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2 text-left">
                      <h3 className="font-semibold text-blue-600">Self-Awareness</h3>
                      <p className="text-sm text-gray-600">Pick traits you see in yourself, then compare with how others describe you to reveal hidden strengths & blind spots.</p>
                    </div>
                  )}

                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm text-purple-800">
                      <strong>{activeTest === "ikigai" ? "25 Questions" : "Over 100 traits"}</strong> • Gain insights into your growth journey
                    </p>
                  </div>

                  <Button
                    onClick={() => {
                      // Mark free test as used when user actually starts the test
                      try {
                        const userStr = localStorage.getItem('user');
                        const user = userStr ? JSON.parse(userStr) : null;
                        const isPremium = user && (user.role === 'premium' || user.role === 'admin');
                        if (!isPremium) {
                          localStorage.setItem('freeTestUsed', '1');
                        }
                      } catch (_) {}
                      setShowIntro(false);
                    }}
                    className="w-full text-lg py-6 bg-purple-600 hover:bg-purple-700"
                    size="lg"
                  >
                    Start Test <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      <div className="fixed top-0 left-0 h-screen w-64 bg-white shadow-md z-10">
        <Sidebar />
      </div>
      <div className="ml-64 flex-1">

        {isResultView ? (
          <div>
            <Navbar toggleSidebar={toggleSidebar} />

            <div className="mb-6 mt-7 text-center">
              <h1 className="text-3xl font-bold">
                {activeTest === "ikigai" ? "Your IKIGAI Finder" : "Your Johari Window"}
              </h1>
            </div>

            {activeTest === "ikigai" ? (
              <>
                <div className="flex gap-4 mb-4 items-center justify-center">
                  <div className="flex rounded-lg border border-purple-300 p-1 bg-white w-1/3">
                    <button onClick={() => showTab("matrix")} className={`px-4 py-2 rounded-md transition-all font-medium ${activeTab === "matrix" ? "bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-md" : "text-black hover:text-purple-600"}`} style={{ width: '100%' }}>Result Matrix</button>
                    <button onClick={() => showTab("venn")} className={`px-4 py-2 rounded-md transition-all font-medium ${activeTab === "venn" ? "bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-md" : "text-black hover:text-purple-600"}`} style={{ width: '100%' }}>Venn Diagram</button>
                  </div>
                  {/* {bothCompleted && <button onClick={() => showTab("development")} className={`px-4 py-2 rounded ${activeTab === "development" ? "bg-purple-600 text-white" : "bg-gray-200"}`}>Development Plan</button>} */}
                </div>
                {activeTab === "matrix" && <ResultMatrix questions={ikigaiQuestions} answers={ikigaiAnswers} />}
                {activeTab === "venn" && <IkigaiVennChart questions={ikigaiQuestions} answers={ikigaiAnswers} />}
                {/* {activeTab === "development" && bothCompleted && (
                  <DevelopmentPlan
                    ikigaiQuestions={ikigaiQuestions}
                    ikigaiAnswers={ikigaiAnswers}
                    johariSelfAnswers={johariSelfAnswers}
                    johariOthersAnswers={johariOthersAnswers}
                  />
                )} */}
              </>
            ) : (
              <>
                {/* <div className="flex gap-4 mb-4 items-center justify-center">
                  <button onClick={() => showTab("results")} className={`px-4 py-2 rounded ${activeTab === "results" ? "bg-blue-600 text-white" : "bg-gray-200"}`}>Johari Window</button>
                </div> */}
                {activeTest === "johari" && (
                  <div className="p-6">
                    <JohariWindowResults
                      selfSelected={johariSelfAnswers}
                      othersSelected={johariOthersAnswers}
                      allAdjectives={johariAdjectives}
                      userId={userId}
                    />
                    {shareableLink && (
                      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded">
                        <div className="font-bold mb-2">Share with Others</div>
                        <div className="flex gap-2 items-center">
                          <input type="text" value={shareableLink} readOnly className="flex-1 p-2 border rounded" />
                          <button onClick={copyShareableLink} className="px-3 py-2 bg-white border rounded">Copy</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

              </>
            )}

            <div className="text-center mt-8 mb-4">
              <button onClick={handleReset} className="px-4 py-2 bg-gradient-to-r rounded mr-4 from-pink-500 to-purple-600 text-white shadow-md">Start Over</button>
              {/* <button onClick={() => switchTest(activeTest === "ikigai" ? "johari" : "ikigai")} className="px-4 py-2 border border-purple-300 p-1 bg-white rounded">
                {activeTest === "ikigai" ? "Go to Johari" : "Go to IKIGAI"}
              </button> */}
            </div>
          </div>
        ) : (
          <div>
            <Navbar toggleSidebar={toggleSidebar} />
            <div className="mb-6 text-center mt-7">
              <h1 className="text-3xl font-bold">
                {activeTest === "ikigai" ? "IKIGAI Finder" : "Johari Window"}
              </h1>
              <p className="text-gray-600">
                {activeTest === "ikigai"
                  ? ""
                  : "Help participants uncover their purpose, hidden traits, and aspects of themselves they may not be fully aware of"}
              </p>
            </div>
            {activeTest === "ikigai" ? (
              <IkigaiFillInBlank onComplete={handleIkigaiComplete} />
            ) : (
              <JohariAdjectiveSelection adjectives={johariAdjectives} onComplete={handleJohariSelfComplete} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
